import { CustomDialog } from "@/components/ui-elements";
import type { CreateWorkflowDTO } from "@/features/workflow/api/createWorkflow";
import { useCreateWorkflow } from "@/features/workflow/api/createWorkflow";
import { useCreateWorkflowInFolder } from "@/features/workflow/api/createWorkflowInFolder";
import { useSorter } from "@/hooks/useSorter";
import { useAuthStore } from "@/store/auth/authUserStore";
import useWorkflowStore from "@/store/workflow/workflowStore";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import type { ForwardRefRenderFunction } from "react";
import { forwardRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const AddRulePopupComponent: ForwardRefRenderFunction<
  HTMLDivElement,
  object
> = () => {
  const { isUseSorter } = useSorter();
  const { user } = useAuthStore.getState();
  const useCreateWorkflowMutation = useCreateWorkflow({});
  const useCreateWorkflowInFolderMutation = useCreateWorkflowInFolder({});

  const { isAddRulePopupOpen, closeAddRulePopup, selectedFolderId } =
    useWorkflowStore();
  const [groupName, setGroupName] = useState("");
  const [workflowType, setWorkflowType] = useState<"ocr" | "sorting">("ocr");
  const handleChangeWorkflowType = (workflowType: "ocr" | "sorting") => {
    setWorkflowType(workflowType);
  };

  const navigation = useNavigate();

  const handleNext = async () => {
    try {
      let res;
      const workflowData = {
        workflow_name: groupName,
        workflow_type: workflowType,
      };

      if (selectedFolderId) {
        res = await useCreateWorkflowInFolderMutation.mutateAsync({
          data: {
            ...workflowData,
            folder_id: selectedFolderId,
          },
        });
      } else {
        const createWorkflow: CreateWorkflowDTO = {
          data: workflowData,
        };
        res = await useCreateWorkflowMutation.mutateAsync(createWorkflow);
      }

      const { id, workflow_type } = res;
      if (workflow_type === "ocr") {
        navigation(`/workflow/${id}`);
      } else {
        navigation(`/${id}/process/documentSetting`);
      }

      closeAddRulePopup();
    } catch (error) {
      // Error handling for workflow creation failure
    }
  };

  return (
    <CustomDialog
      title="Scan設定を追加"
      description="Scan設定を追加します。ルール名を登録してください。"
      open={isAddRulePopupOpen}
      onClose={closeAddRulePopup}
      primaryButtonText="登録する"
      primaryButtonAction={handleNext}
      primaryButtonDisabled={groupName === ""}
      secondaryButtonText="キャンセル"
      secondaryButtonAction={closeAddRulePopup}
    >
      <TextField
        autoFocus
        margin="dense"
        id="name"
        placeholder="請求書"
        type="text"
        fullWidth
        value={groupName}
        onChange={(e) => setGroupName(e.target.value)}
        label="ルール名"
      />
      {isUseSorter && user?.organization.is_sort_function_enabled && (
        <FormControl component="fieldset" style={{ marginTop: "20px" }}>
          <FormLabel component="legend">処理タイプ</FormLabel>
          <RadioGroup
            aria-label="workflow-type"
            name="workflow-type"
            value={workflowType}
            onChange={(e) =>
              handleChangeWorkflowType(e.target.value as "sorting" | "ocr")
            }
          >
            <FormControlLabel value="ocr" control={<Radio />} label="Scan" />
            <FormControlLabel
              value="sorting"
              control={<Radio />}
              label="ドキュメント分析"
            />
          </RadioGroup>
        </FormControl>
      )}
    </CustomDialog>
  );
};

const AddRulePopup = forwardRef(AddRulePopupComponent);
AddRulePopup.displayName = "AddRulePopup";

export default AddRulePopup;
